import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { getDashboardStats, getEBCIWeather, getEBCINotams, getGeneralNotes, updateGeneralNotes, getOperationalNotes, updateOperationalNotes, getOperationalNotesFromFlights, getPastOperationalNotesFromFlights, getStandClosures, getOperationalRestrictions } from "@/lib/api";
const RadarMap = lazy(() => import("@/components/RadarMap"));
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Plane, Users, Calendar, Clock, ArrowRight, Loader2, Wind, Cloud, Thermometer, AlertTriangle, Eye, Droplets, RefreshCw, Snowflake, FileText, Download, StickyNote, Save, Pencil, History, Ban, CalendarClock, Radio, X, CloudRain, CloudSnow, CloudFog, Zap, Sparkles, Search } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
// Chart removed per user request

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [weather, setWeather] = useState(null);
  const [notams, setNotams] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [lastWeatherUpdate, setLastWeatherUpdate] = useState(null);
  const [generalNotes, setGeneralNotes] = useState("");
  const [notesEditing, setNotesEditing] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesLastUpdate, setNotesLastUpdate] = useState(null);
  const [operationalNotes, setOperationalNotes] = useState("");
  const [opNotesEditing, setOpNotesEditing] = useState(false);
  const [opNotesSaving, setOpNotesSaving] = useState(false);
  const [opNotesLastUpdate, setOpNotesLastUpdate] = useState(null);
  const [flightNotes, setFlightNotes] = useState([]);
  const [pastNotesOpen, setPastNotesOpen] = useState(false);
  const [dashboardYear, setDashboardYear] = useState("2026");
  const [noteYear, setNoteYear] = useState("all");
  const [noteMonth, setNoteMonth] = useState("all");
  const [noteSearch, setNoteSearch] = useState("");
  const [pastNotes, setPastNotes] = useState([]);
  const [pastNotesLoading, setPastNotesLoading] = useState(false);
  const [standClosures, setStandClosures] = useState([]);
  const [restrictions, setRestrictions] = useState([]);
  const [radarOpen, setRadarOpen] = useState(false);
  const navigate = useNavigate();

  // Fetch stand closures
  const fetchClosures = useCallback(async () => {
    try {
      const res = await getStandClosures();
      setStandClosures(res.data.closures || []);
    } catch (error) {
      console.error("Failed to fetch stand closures:", error);
    }
  }, []);

  // Fetch operational restrictions
  const fetchRestrictions = useCallback(async () => {
    try {
      const res = await getOperationalRestrictions();
      setRestrictions(res.data.restrictions || []);
    } catch (error) {
      console.error("Failed to fetch operational restrictions:", error);
    }
  }, []);

  const fetchGeneralNotes = useCallback(async () => {
    try {
      const res = await getGeneralNotes();
      setGeneralNotes(res.data.content || "");
      if (res.data.updated_at) {
        setNotesLastUpdate({
          time: res.data.updated_at,
          by: res.data.updated_by
        });
      }
    } catch (error) {
      console.error("Failed to fetch general notes:", error);
    }
  }, []);

  // Fetch operational notes from flights (auto-generated)
  const fetchFlightNotes = useCallback(async () => {
    try {
      const res = await getOperationalNotesFromFlights();
      setFlightNotes(res.data.notes || []);
    } catch (error) {
      console.error("Failed to fetch flight notes:", error);
    }
  }, []);

  const handleSaveNotes = async () => {
    setNotesSaving(true);
    try {
      const res = await updateGeneralNotes(generalNotes);
      setNotesLastUpdate({
        time: res.data.updated_at,
        by: res.data.updated_by
      });
      setNotesEditing(false);
      toast.success("Notes saved");
    } catch (error) {
      toast.error("Failed to save notes");
    } finally {
      setNotesSaving(false);
    }
  };

  const fetchOperationalNotes = useCallback(async () => {
    try {
      const res = await getOperationalNotes();
      setOperationalNotes(res.data.content || "");
      if (res.data.updated_at) {
        setOpNotesLastUpdate({
          time: res.data.updated_at,
          by: res.data.updated_by
        });
      }
    } catch (error) {
      console.error("Failed to fetch operational notes:", error);
    }
  }, []);

  const handleSaveOperationalNotes = async () => {
    setOpNotesSaving(true);
    try {
      const res = await updateOperationalNotes(operationalNotes);
      setOpNotesLastUpdate({
        time: res.data.updated_at,
        by: res.data.updated_by
      });
      setOpNotesEditing(false);
      toast.success("Operational notes saved");
    } catch (error) {
      toast.error("Failed to save operational notes");
    } finally {
      setOpNotesSaving(false);
    }
  };

  const fetchPastNotes = async () => {
    setPastNotesLoading(true);
    try {
      const res = await getPastOperationalNotesFromFlights();
      setPastNotes(res.data.notes || []);
    } catch (error) {
      console.error("Failed to fetch past notes:", error);
      toast.error("Failed to load past notes");
    } finally {
      setPastNotesLoading(false);
    }
  };

  const handleOpenPastNotes = () => {
    setPastNotesOpen(true);
    fetchPastNotes();
  };

  const fetchWeatherAndNotams = useCallback(async () => {
    setWeatherLoading(true);
    try {
      const [weatherRes, notamsRes] = await Promise.all([
        getEBCIWeather().catch(() => ({ data: null })),
        getEBCINotams().catch(() => ({ data: null }))
      ]);
      setWeather(weatherRes.data);
      setNotams(notamsRes.data);
      setLastWeatherUpdate(new Date());
    } catch (error) {
      console.error("Failed to fetch weather/NOTAMs:", error);
    } finally {
      setWeatherLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWeatherAndNotams();
    fetchGeneralNotes();
    fetchOperationalNotes();
    fetchFlightNotes();
    fetchClosures();
    fetchRestrictions();
  }, [fetchWeatherAndNotams, fetchGeneralNotes, fetchOperationalNotes, fetchFlightNotes, fetchClosures, fetchRestrictions]);

  useEffect(() => {
    getDashboardStats(dashboardYear)
      .then((r) => setStats(r.data))
      .catch((e) => console.error("Failed to fetch dashboard data:", e))
      .finally(() => setLoading(false));
  }, [dashboardYear]);

  // Auto-refresh weather & NOTAMs every hour (manual refresh button available)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchWeatherAndNotams();
    }, 3600000); // 1 hour

    return () => clearInterval(interval);
  }, [fetchWeatherAndNotams]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-10 h-10 animate-spin text-[#d4af37]" />
      </div>
    );
  }

  const kpiCards = [
    {
      label: "Total Flights",
      value: stats?.total_flights || 0,
      icon: Plane,
      color: "bg-[#0f172a]",
      iconColor: "text-white",
      link: "/flights?tab=all"
    },
    {
      label: "Total Passengers",
      value: stats?.total_passengers || 0,
      icon: Users,
      color: "bg-[#d4af37]",
      iconColor: "text-[#0f172a]",
      link: null
    },
    {
      label: "Today's Flights",
      value: stats?.todays_flights || 0,
      icon: Calendar,
      color: "bg-emerald-500",
      iconColor: "text-white",
      link: "/global-planning?tab=today"
    },
    {
      label: "Tomorrow's Flights",
      value: stats?.tomorrows_flights || 0,
      icon: Clock,
      color: "bg-amber-500",
      iconColor: "text-white",
      link: "/global-planning?tab=tomorrow"
    }
  ];

  // Parse METAR data
  const parseMetar = () => {
    if (!weather?.metar) return null;
    const metar = weather.metar;
    
    // For aviationweather.gov format
    if (metar.rawOb) {
      return {
        raw: metar.rawOb,
        temp: metar.temp,
        dewp: metar.dewp,
        wdir: metar.wdir,
        wspd: metar.wspd,
        wgst: metar.wgst,
        visib: metar.visib,
        cover: metar.cover,
        wx: metar.wxString,
        altim: metar.altim,
        obsTime: metar.obsTime
      };
    }
    
    // For CheckWX format
    return {
      raw: metar.raw_text || metar.raw,
      temp: metar.temperature?.celsius,
      dewp: metar.dewpoint?.celsius,
      wdir: metar.wind?.degrees,
      wspd: metar.wind?.speed_kts,
      wgst: metar.wind?.gust_kts,
      visib: metar.visibility?.meters_float ? metar.visibility.meters_float / 1000 : metar.visibility?.miles,
      cover: metar.clouds?.[0]?.code,
      wx: metar.conditions?.map(c => c.text).join(', '),
      altim: metar.barometer?.hpa,
      obsTime: metar.observed
    };
  };

  // Parse TAF data
  const parseTaf = () => {
    if (!weather?.taf) return null;
    const taf = weather.taf;
    
    if (taf.rawTAF) {
      return { raw: taf.rawTAF };
    }
    return { raw: taf.raw_text || taf.raw || null };
  };

  const metarData = parseMetar();
  const tafData = parseTaf();
  const snowtamData = weather?.snowtam;

  // Get wind direction as compass
  const getWindDirection = (degrees) => {
    if (!degrees && degrees !== 0) return "VRB";
    const directions = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];
    const index = Math.round(degrees / 22.5) % 16;
    return directions[index];
  };

  // Translate METAR to plain language
  const translateMetar = (metar) => {
    if (!metar?.raw) return null;
    
    const translations = [];
    const raw = metar.raw;
    
    // Airport and Time
    const timeMatch = raw.match(/(\d{2})(\d{2})(\d{2})Z/);
    if (timeMatch) {
      translations.push(`📍 Observation taken at ${timeMatch[2]}:${timeMatch[3]} UTC on day ${timeMatch[1]}`);
    }
    
    // Wind
    if (metar.wspd !== undefined) {
      let windText = "";
      if (metar.wspd === 0) {
        windText = "💨 Wind: Calm (no wind)";
      } else {
        const dir = getWindDirection(metar.wdir);
        windText = `💨 Wind: From ${dir} (${metar.wdir}°) at ${metar.wspd} knots`;
        if (metar.wgst) {
          windText += `, gusting to ${metar.wgst} knots`;
        }
      }
      // Check for variable wind
      const varMatch = raw.match(/(\d{3})V(\d{3})/);
      if (varMatch) {
        windText += ` (varying between ${varMatch[1]}° and ${varMatch[2]}°)`;
      }
      translations.push(windText);
    }
    
    // Visibility
    if (raw.includes('CAVOK')) {
      translations.push("👁️ Visibility: CAVOK — 10+ km, no significant cloud or weather");
    } else if (metar.visib !== undefined && metar.visib !== null) {
      const v = typeof metar.visib === 'string' && metar.visib.includes('+') ? 10 : parseFloat(metar.visib);
      let visText = "";
      if (Number.isNaN(v)) {
        visText = "👁️ Visibility: Available below (see raw report)";
      } else if (v >= 10) {
        visText = "👁️ Visibility: Excellent (10+ kilometers)";
      } else if (v >= 5) {
        visText = `👁️ Visibility: Good (${v} kilometers)`;
      } else if (v >= 1) {
        visText = `👁️ Visibility: Moderate (${v} kilometers)`;
      } else {
        visText = `⚠️ Visibility: POOR (${Math.round(v * 1000)} meters) - Low Visibility Operations may apply`;
      }
      translations.push(visText);
    }
    
    // RVR (Runway Visual Range)
    const rvrMatch = raw.match(/R(\d{2}[LRC]?)\/([PM]?)(\d{4})(N|U|D)?/g);
    if (rvrMatch) {
      rvrMatch.forEach(rvr => {
        const parts = rvr.match(/R(\d{2}[LRC]?)\/([PM]?)(\d{4})(N|U|D)?/);
        if (parts) {
          const runway = parts[1];
          const prefix = parts[2] === 'P' ? 'More than ' : parts[2] === 'M' ? 'Less than ' : '';
          const value = parts[3];
          const trend = parts[4] === 'U' ? ' (improving)' : parts[4] === 'D' ? ' (decreasing)' : parts[4] === 'N' ? ' (no change)' : '';
          translations.push(`🛬 Runway ${runway} Visual Range: ${prefix}${value} meters${trend}`);
        }
      });
    }
    
    // Weather Phenomena
    const wxCodes = {
      'FG': '🌫️ Fog',
      'BR': '🌫️ Mist',
      'HZ': '🌫️ Haze',
      'RA': '🌧️ Rain',
      'DZ': '🌧️ Drizzle',
      'SN': '❄️ Snow',
      'SG': '❄️ Snow Grains',
      'GR': '🧊 Hail',
      'GS': '🧊 Small Hail',
      'TS': '⛈️ Thunderstorm',
      'SH': '🌦️ Showers',
      'FZ': '🥶 Freezing',
      'FZRA': '🥶 Freezing Rain',
      'FZFG': '🥶 Freezing Fog',
      '+': 'Heavy ',
      '-': 'Light ',
      'VC': 'In Vicinity '
    };
    
    // Check for weather in raw METAR
    const wxPatterns = ['\\+?-?VC?(TS|SH|FZ)?(RA|SN|DZ|GR|GS|FG|BR|HZ|FU|SA|DU)+'];
    const foundWx = [];
    if (raw.includes('FG')) foundWx.push('🌫️ FOG present - Reduced visibility');
    if (raw.includes('BR')) foundWx.push('🌫️ MIST present - Slight visibility reduction');
    if (raw.includes('RA')) foundWx.push('🌧️ RAIN');
    if (raw.includes('SN')) foundWx.push('❄️ SNOW');
    if (raw.includes('TS')) foundWx.push('⛈️ THUNDERSTORM');
    if (raw.includes('FZRA')) foundWx.push('🥶 FREEZING RAIN - Dangerous icing conditions');
    if (raw.includes('FZFG')) foundWx.push('🥶 FREEZING FOG - Icing possible');
    
    if (foundWx.length > 0) {
      translations.push(`🌤️ Weather: ${foundWx.join(', ')}`);
    } else if (!raw.includes('FG') && !raw.includes('BR') && !raw.includes('RA') && !raw.includes('SN')) {
      translations.push("🌤️ Weather: No significant weather phenomena");
    }
    
    // Cloud Cover
    const cloudCodes = {
      'SKC': 'Sky Clear',
      'CLR': 'Clear below 12,000ft',
      'FEW': 'Few clouds (1-2 oktas)',
      'SCT': 'Scattered clouds (3-4 oktas)',
      'BKN': 'Broken clouds (5-7 oktas)',
      'OVC': 'Overcast (8 oktas - full cloud cover)',
      'VV': 'Vertical visibility (sky obscured)'
    };
    
    const cloudMatch = raw.match(/(SKC|CLR|FEW|SCT|BKN|OVC|VV)(\d{3})?/g);
    if (cloudMatch) {
      const cloudDesc = cloudMatch.map(c => {
        const parts = c.match(/(SKC|CLR|FEW|SCT|BKN|OVC|VV)(\d{3})?/);
        if (parts) {
          const type = cloudCodes[parts[1]] || parts[1];
          const height = parts[2] ? ` at ${parseInt(parts[2]) * 100} feet` : '';
          return `${type}${height}`;
        }
        return c;
      });
      translations.push(`☁️ Clouds: ${cloudDesc.join(', ')}`);
    }
    
    // Temperature and Dewpoint
    if (metar.temp !== undefined) {
      let tempText = `🌡️ Temperature: ${metar.temp}°C`;
      if (metar.dewp !== undefined) {
        const spread = metar.temp - metar.dewp;
        tempText += `, Dewpoint: ${metar.dewp}°C`;
        if (spread <= 2) {
          tempText += " ⚠️ (Fog/mist likely - temp near dewpoint)";
        }
      }
      translations.push(tempText);
    }
    
    // Pressure
    if (metar.altim) {
      let pressText = `📊 Pressure (QNH): ${metar.altim} hPa`;
      if (metar.altim < 1000) {
        pressText += " (Low pressure)";
      } else if (metar.altim > 1025) {
        pressText += " (High pressure)";
      }
      translations.push(pressText);
    }
    
    return translations;
  };

  const metarTranslation = translateMetar(metarData);

  // Translate TAF to plain language
  const translateTaf = (taf) => {
    if (!taf?.raw) return null;
    
    const translations = [];
    const raw = taf.raw;
    
    // Extract validity period
    const validMatch = raw.match(/(\d{2})(\d{2})\/(\d{2})(\d{2})/);
    if (validMatch) {
      translations.push(`📅 Valid: Day ${validMatch[1]} at ${validMatch[2]}:00 UTC to Day ${validMatch[3]} at ${validMatch[4]}:00 UTC`);
    }
    
    // Base forecast
    const baseWind = raw.match(/TAF[^]*?(\d{3})(\d{2,3})(G\d{2,3})?KT/);
    if (baseWind) {
      let windText = `💨 Initial Wind: From ${baseWind[1]}° at ${baseWind[2]} knots`;
      if (baseWind[3]) {
        windText += `, gusting to ${baseWind[3].replace('G', '')} knots`;
      }
      translations.push(windText);
    }
    
    // Visibility in base
    const baseVis = raw.match(/KT\s+(\d{4})\s/);
    if (baseVis) {
      const vis = parseInt(baseVis[1]);
      if (vis >= 9999) {
        translations.push("👁️ Initial Visibility: 10+ km (excellent)");
      } else {
        translations.push(`👁️ Initial Visibility: ${vis} meters`);
      }
    }
    
    // TEMPO periods (temporary changes)
    const tempoMatches = raw.match(/TEMPO\s+(\d{4}\/\d{4})\s+([^TEMPO|BECMG|PROB|FM]+)/g);
    if (tempoMatches) {
      tempoMatches.forEach((tempo, idx) => {
        const parts = tempo.match(/TEMPO\s+(\d{2})(\d{2})\/(\d{2})(\d{2})\s+(.*)/);
        if (parts) {
          let conditions = parts[5].trim();
          // Translate weather codes
          conditions = conditions.replace(/FG/g, 'fog').replace(/BR/g, 'mist').replace(/RA/g, 'rain')
            .replace(/SN/g, 'snow').replace(/TS/g, 'thunderstorms').replace(/BKN/g, 'broken clouds')
            .replace(/OVC/g, 'overcast').replace(/SCT/g, 'scattered clouds').replace(/FEW/g, 'few clouds');
          translations.push(`⏱️ TEMPORARY (${parts[2]}:00-${parts[4]}:00 UTC): ${conditions}`);
        }
      });
    }
    
    // BECMG periods (becoming - permanent change)
    const becmgMatches = raw.match(/BECMG\s+(\d{4}\/\d{4})\s+([^TEMPO|BECMG|PROB|FM]+)/g);
    if (becmgMatches) {
      becmgMatches.forEach((becmg, idx) => {
        const parts = becmg.match(/BECMG\s+(\d{2})(\d{2})\/(\d{2})(\d{2})\s+(.*)/);
        if (parts) {
          let conditions = parts[5].trim();
          conditions = conditions.replace(/FG/g, 'fog').replace(/BR/g, 'mist').replace(/RA/g, 'rain')
            .replace(/SN/g, 'snow').replace(/NSW/g, 'no significant weather').replace(/CAVOK/g, 'clear skies, visibility OK');
          translations.push(`🔄 BECOMING (${parts[2]}:00-${parts[4]}:00 UTC): ${conditions}`);
        }
      });
    }
    
    // PROB (probability)
    const probMatches = raw.match(/PROB(\d{2})\s+TEMPO\s+(\d{4}\/\d{4})\s+([^TEMPO|BECMG|PROB|FM]+)/g);
    if (probMatches) {
      probMatches.forEach((prob) => {
        const parts = prob.match(/PROB(\d{2})\s+TEMPO\s+(\d{2})(\d{2})\/(\d{2})(\d{2})\s+(.*)/);
        if (parts) {
          let conditions = parts[6].trim();
          conditions = conditions.replace(/FG/g, 'fog').replace(/BR/g, 'mist').replace(/RA/g, 'rain')
            .replace(/TSRA/g, 'thunderstorms with rain').replace(/TS/g, 'thunderstorms');
          translations.push(`🎲 ${parts[1]}% PROBABILITY (${parts[3]}:00-${parts[5]}:00 UTC): ${conditions}`);
        }
      });
    }
    
    return translations;
  };

  const tafTranslation = translateTaf(tafData);

  // Translate SNOWTAM to plain language
  const translateSnowtam = (snowtam) => {
    if (!snowtam?.raw) return null;
    
    const translations = [];
    const raw = snowtam.raw;
    
    translations.push("❄️ SNOWTAM - Snow/Ice Runway Condition Report");
    
    // Extract aerodrome
    const aeroMatch = raw.match(/SNOWTAM\s+\d+\s*([A-Z]{4})/i) || raw.match(/([A-Z]{4})/);
    if (aeroMatch) {
      translations.push(`📍 Aerodrome: ${aeroMatch[1]}`);
    }
    
    // Runway conditions - format: RWY nn/XXXXX/XXXXX/XXXXX
    const rwyMatches = raw.match(/R(?:WY)?\s*(\d{2}[LRC]?)[\s\/]+(\d+)[\s\/]+(\d+)/g);
    if (rwyMatches) {
      rwyMatches.forEach(rwy => {
        const parts = rwy.match(/R(?:WY)?\s*(\d{2}[LRC]?)[\s\/]+(\d+)[\s\/]+(\d+)/);
        if (parts) {
          translations.push(`🛬 Runway ${parts[1]}: Condition code ${parts[2]}, Braking action ${parts[3]}`);
        }
      });
    }
    
    // Depth of deposit
    const depthMatch = raw.match(/(\d+)\s*MM/i);
    if (depthMatch) {
      translations.push(`📏 Deposit Depth: ${depthMatch[1]} mm`);
    }
    
    // Type of deposit codes
    const depositTypes = {
      'DRY SNOW': '🌨️ Dry Snow',
      'WET SNOW': '🌨️ Wet Snow',
      'COMPACTED SNOW': '❄️ Compacted Snow',
      'ICE': '🧊 Ice',
      'SLUSH': '💧 Slush',
      'STANDING WATER': '💧 Standing Water',
      'FROST': '🥶 Frost',
      'DRY': '✅ Dry (no contamination)'
    };
    
    Object.entries(depositTypes).forEach(([code, desc]) => {
      if (raw.toUpperCase().includes(code)) {
        translations.push(`Surface: ${desc}`);
      }
    });
    
    // Braking action
    if (raw.includes('GOOD')) translations.push("🟢 Braking Action: GOOD");
    else if (raw.includes('MEDIUM')) translations.push("🟡 Braking Action: MEDIUM - Exercise caution");
    else if (raw.includes('POOR')) translations.push("🔴 Braking Action: POOR - Significant braking reduction");
    
    // Validity
    const validMatch = raw.match(/VALID\s+(?:UNTIL\s+)?(\d{2})(\d{2})(\d{2})/i);
    if (validMatch) {
      translations.push(`⏰ Valid until: Day ${validMatch[1]} at ${validMatch[2]}:${validMatch[3]} UTC`);
    }
    
    if (translations.length === 1) {
      translations.push("See raw SNOWTAM below for full details");
    }
    
    return translations;
  };

  const snowtamTranslation = translateSnowtam(snowtamData);

  // ===== Forecast outlook + hazard warnings (from backend) =====
  const forecast = weather?.forecast;
  const ai = weather?.ai;
  const hazards = forecast?.hazards || [];

  // Hazard type -> icon + color styling
  const HAZARD_META = {
    thunderstorm: { Icon: Zap, color: "text-purple-600", chip: "bg-purple-100 text-purple-700 border-purple-300" },
    rain: { Icon: CloudRain, color: "text-blue-600", chip: "bg-blue-100 text-blue-700 border-blue-300" },
    snow: { Icon: CloudSnow, color: "text-sky-600", chip: "bg-sky-100 text-sky-700 border-sky-300" },
    freezing: { Icon: Snowflake, color: "text-cyan-600", chip: "bg-cyan-100 text-cyan-700 border-cyan-300" },
    fog: { Icon: CloudFog, color: "text-slate-600", chip: "bg-slate-100 text-slate-700 border-slate-300" },
    low_vis: { Icon: Eye, color: "text-amber-600", chip: "bg-amber-100 text-amber-700 border-amber-300" },
    gusts: { Icon: Wind, color: "text-teal-600", chip: "bg-teal-100 text-teal-700 border-teal-300" },
  };
  const getHazardMeta = (t) => HAZARD_META[t] || { Icon: AlertTriangle, color: "text-amber-600", chip: "bg-amber-100 text-amber-700 border-amber-300" };
  const topSeverity = hazards.some(h => h.severity === "high") ? "high" : hazards.some(h => h.severity === "medium") ? "medium" : "low";
  const bannerStyle = topSeverity === "high"
    ? "bg-red-50 border-red-400 text-red-800"
    : topSeverity === "medium"
    ? "bg-amber-50 border-amber-400 text-amber-800"
    : "bg-yellow-50 border-yellow-300 text-yellow-800";

  // Clean visibility label (fixes CAVOK / "6+" NaN bug)
  const visibilityLabel = (() => {
    const raw = metarData?.raw || "";
    if (/\bCAVOK\b/.test(raw)) return "CAVOK";
    const v = metarData?.visib;
    if (v === undefined || v === null) return "N/A";
    if (typeof v === "string" && v.includes("+")) return "10+ km";
    const num = parseFloat(v);
    if (Number.isNaN(num)) return "N/A";
    return num >= 10 ? "10+ km" : `${num} km`;
  })();

  // Prefer AI plain-language; fall back to deterministic translation
  const metarLines = (ai?.metar?.length ? ai.metar : metarTranslation) || [];
  const tafLines = (ai?.taf?.length ? ai.taf : tafTranslation) || [];
  const snowtamLines = (ai?.snowtam?.length ? ai.snowtam : snowtamTranslation) || [];
  const aiActive = !!(ai && (ai.metar?.length || ai.taf?.length || ai.snowtam?.length));

  const pastNoteYears = [...new Set(pastNotes.map((n) => (n.date || "").slice(0, 4)).filter(Boolean))].sort().reverse();
  const filteredPastNotes = pastNotes.filter((n) => {
    const date = n.date || "";
    if (noteYear !== "all" && date.slice(0, 4) !== noteYear) return false;
    if (noteMonth !== "all" && date.slice(5, 7) !== noteMonth) return false;
    if (noteSearch.trim()) {
      const q = noteSearch.trim().toLowerCase();
      if (!(`${n.registration} ${n.note}`.toLowerCase().includes(q))) return false;
    }
    return true;
  });

  // ===== Daily Plannings archive filters =====

  return (
    <div className="animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#0f172a] font-['Outfit']">Dashboard</h1>
          <p className="text-[#64748b] mt-1">Welcome to Charleroi South Brussels Airport - Business Aviation</p>
        </div>
        <Select value={dashboardYear} onValueChange={setDashboardYear}>
          <SelectTrigger className="w-28 h-9 bg-white" data-testid="dashboard-year-selector">
            <span className="text-xs text-[#64748b] mr-1">Year</span>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {["2026", "2025", "2024", "2023", "2022"].map((y) => (
              <SelectItem key={y} value={y}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* KPI Cards */}
      <div className="dashboard-grid mb-6">
        {kpiCards.map((card, index) => (
          <Card 
            key={card.label} 
            className={`kpi-card card-hover border-[#e2e8f0] bg-white ${card.link ? 'cursor-pointer' : ''}`}
            style={{ animationDelay: `${index * 100}ms` }}
            data-testid={`kpi-${card.label.toLowerCase().replace(/\s+/g, '-')}`}
            onClick={() => card.link && navigate(card.link)}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[#64748b] text-sm font-medium mb-1">{card.label}</p>
                  <p className="text-4xl font-bold text-[#0f172a] font-['Outfit']">
                    {card.value.toLocaleString()}
                  </p>
                  {card.link && (
                    <p className="text-xs text-[#d4af37] mt-2 flex items-center gap-1">
                      <ArrowRight size={12} /> Click to view
                    </p>
                  )}
                </div>
                <div className={`stat-icon ${card.color}`}>
                  <card.icon size={24} className={card.iconColor} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* General Notes Section - Spans width of first two KPI cards */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
        <Card className="lg:col-span-2 border-[#e2e8f0] bg-white" data-testid="general-notes-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-[#0f172a] font-['Outfit']">
                <StickyNote size={20} className="text-[#d4af37]" />
                General Notes
              </CardTitle>
              <div className="flex items-center gap-2">
                {notesLastUpdate && (
                  <span className="text-xs text-[#64748b]">
                    Last updated by {notesLastUpdate.by}
                  </span>
                )}
                {notesEditing ? (
                  <Button
                    size="sm"
                    onClick={handleSaveNotes}
                    disabled={notesSaving}
                    className="bg-[#d4af37] hover:bg-[#b8972e] text-[#0f172a]"
                    data-testid="save-notes-btn"
                  >
                    {notesSaving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />}
                    Save
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setNotesEditing(true)}
                    className="border-[#e2e8f0]"
                    data-testid="edit-notes-btn"
                  >
                    <Pencil size={14} className="mr-1" />
                    Edit
                  </Button>
                )}
              </div>
            </div>
            <p className="text-xs text-[#64748b]">Visible to all users</p>
          </CardHeader>
          <CardContent>
            {/* Operational Restrictions Alert - severity colored */}
            {restrictions.filter(r => r.status === "active" || r.status === "upcoming").length > 0 && (
              <div className="mb-3 space-y-2" data-testid="dashboard-restriction-alert">
                {restrictions
                  .filter(r => r.status === "active" || r.status === "upcoming")
                  .map((r) => {
                    const sev = r.severity || "warning";
                    const styles = sev === "critical"
                      ? "bg-red-50 dark:bg-red-900/30 border-red-500 text-red-700 dark:text-red-300"
                      : sev === "info"
                        ? "bg-sky-50 dark:bg-sky-900/30 border-sky-500 text-sky-700 dark:text-sky-300"
                        : "bg-amber-50 dark:bg-amber-900/30 border-amber-500 text-amber-700 dark:text-amber-300";
                    const badge = sev === "critical" ? "bg-red-600" : sev === "info" ? "bg-sky-500" : "bg-amber-500";
                    return (
                      <div key={r.id} className={`p-3 border-2 rounded-lg ${styles}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <AlertTriangle size={18} />
                          <span className="font-bold">Operational Restriction</span>
                          <Badge className={`${badge} text-white uppercase text-[10px]`}>{sev}</Badge>
                          {r.status === "upcoming" && (
                            <Badge className="bg-amber-500 text-white text-[10px]">Upcoming</Badge>
                          )}
                        </div>
                        <div className="text-sm font-semibold">{r.reason}</div>
                        <div className="text-xs opacity-80 mt-0.5">
                          {format(parseISO(r.start_date), "MMM d")} - {format(parseISO(r.end_date), "MMM d, yyyy")}
                          {" · 🕒 "}
                          {(r.start_time && r.end_time)
                            ? `${r.start_time}–${r.end_time}${r.start_time >= r.end_time ? " next day" : ""} local`
                            : "All day"}
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}

            {/* Stand Closures Alert - In RED */}
            {standClosures.filter(c => c.status === "active" || c.status === "upcoming").length > 0 && (
              <div 
                className="mb-3 p-3 bg-red-50 dark:bg-red-900/30 border-2 border-red-500 rounded-lg"
                data-testid="dashboard-closure-alert"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Ban size={18} className="text-red-600" />
                  <span className="font-bold text-red-700 dark:text-red-400">Stand Closures</span>
                </div>
                <div className="space-y-1">
                  {standClosures
                    .filter(c => c.status === "active" || c.status === "upcoming")
                    .map((closure) => (
                      <div 
                        key={closure.id}
                        className={`flex items-center gap-2 text-sm ${
                          closure.status === "active" 
                            ? "text-red-700 dark:text-red-300 font-semibold" 
                            : "text-amber-700 dark:text-amber-300"
                        }`}
                      >
                        <Badge className={closure.status === "active" ? "bg-red-600" : "bg-amber-500"}>
                          {closure.stand_id}
                        </Badge>
                        <span>
                          {closure.status === "active" ? "CLOSED" : "Scheduled"}: {format(parseISO(closure.start_date), "MMM d")} - {format(parseISO(closure.end_date), "MMM d")}
                        </span>
                        {closure.reason && <span className="text-gray-500 italic">({closure.reason})</span>}
                      </div>
                    ))}
                </div>
              </div>
            )}
            
            {notesEditing ? (
              <Textarea
                value={generalNotes}
                onChange={(e) => setGeneralNotes(e.target.value)}
                placeholder="Enter general notes here... (e.g., important announcements, reminders, shift handover notes)"
                className="min-h-[100px] border-[#e2e8f0] resize-none"
                data-testid="notes-textarea"
              />
            ) : (
              <div 
                className="min-h-[100px] p-3 bg-[#f8fafc] dark:bg-[#0f172a] rounded-lg text-sm text-[#0f172a] dark:text-white whitespace-pre-wrap"
                data-testid="notes-display"
              >
                {generalNotes || <span className="text-[#94a3b8] italic">No notes yet. Click Edit to add notes.</span>}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Operational Notes - Under Today's and Tomorrow's Flights */}
        <Card className="lg:col-span-2 border-[#e2e8f0] bg-white" data-testid="operational-notes-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-[#0f172a] font-['Outfit']">
                <StickyNote size={20} className="text-emerald-500" />
                Operational Notes
                {flightNotes.length > 0 && (
                  <Badge className="bg-emerald-100 text-emerald-700 ml-2">{flightNotes.length} from flights</Badge>
                )}
              </CardTitle>
              <div className="flex items-center gap-2">
                {opNotesLastUpdate && (
                  <span className="text-xs text-[#64748b]">
                    Last updated by {opNotesLastUpdate.by}
                  </span>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleOpenPastNotes}
                  className="border-[#e2e8f0] text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:border-gray-600 dark:text-amber-400 dark:hover:bg-amber-900/30"
                  data-testid="past-notes-btn"
                >
                  <History size={14} className="mr-1" />
                  Past Notes
                </Button>
                {opNotesEditing ? (
                  <Button
                    size="sm"
                    onClick={handleSaveOperationalNotes}
                    disabled={opNotesSaving}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white"
                    data-testid="save-op-notes-btn"
                  >
                    {opNotesSaving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />}
                    Save
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setOpNotesEditing(true)}
                    className="border-[#e2e8f0]"
                    data-testid="edit-op-notes-btn"
                  >
                    <Pencil size={14} className="mr-1" />
                    Edit
                  </Button>
                )}
              </div>
            </div>
            <p className="text-xs text-[#64748b]">Auto-populated from flight notes (today onwards) + manual notes</p>
          </CardHeader>
          <CardContent>
            {/* Auto-generated notes from flights */}
            {flightNotes.length > 0 && (
              <div className="mb-4 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                <p className="text-xs text-emerald-700 dark:text-emerald-300 font-semibold mb-2 flex items-center gap-1">
                  <Plane size={12} /> Flight Notes (Today + Upcoming)
                </p>
                <div className="space-y-1">
                  {flightNotes.map((note, idx) => (
                    <div key={idx} className="text-sm text-emerald-800 dark:text-emerald-200 font-mono">
                      <span className="text-emerald-600 dark:text-emerald-400">{note.date}</span>
                      <span className="mx-1">/</span>
                      <span className="font-semibold">{note.registration}</span>
                      <span className="mx-1">-</span>
                      <span>{note.note}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Manual operational notes */}
            {opNotesEditing ? (
              <Textarea
                value={operationalNotes}
                onChange={(e) => setOperationalNotes(e.target.value)}
                placeholder="Enter additional operational notes here... (e.g., daily operations, special instructions, ground handling notes)"
                className="min-h-[100px] border-[#e2e8f0] resize-none"
                data-testid="op-notes-textarea"
              />
            ) : (
              <div 
                className="min-h-[60px] p-3 bg-[#f8fafc] dark:bg-[#0f172a] rounded-lg text-sm text-[#0f172a] dark:text-white whitespace-pre-wrap"
                data-testid="op-notes-display"
              >
                {operationalNotes || <span className="text-[#94a3b8] italic">No additional manual notes. Click Edit to add.</span>}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Weather & NOTAMs Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8 items-stretch">
        {/* EBCI Weather Card with Tabs for METAR/TAF/SNOWTAM */}
        <Card className="border-[#e2e8f0] bg-white flex flex-col">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-[#0f172a] font-['Outfit']">
                <Cloud size={20} className="text-[#d4af37]" />
                EBCI Weather
              </CardTitle>
              <div className="flex items-center gap-2">
                {lastWeatherUpdate && (
                  <span className="text-xs text-[#64748b]">
                    Updated: {format(lastWeatherUpdate, "HH:mm:ss")}
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={fetchWeatherAndNotams}
                  disabled={weatherLoading}
                  className="h-8 w-8"
                  data-testid="refresh-weather-btn"
                >
                  <RefreshCw size={16} className={weatherLoading ? "animate-spin" : ""} />
                </Button>
              </div>
            </div>
            <p className="text-xs text-[#64748b]">Auto-refreshes hourly · use refresh to update now</p>
          </CardHeader>
          <CardContent>
            {/* Upcoming hazard alert banner (next 12h) */}
            {hazards.length > 0 && (
              <div className={`mb-4 rounded-lg border-2 p-3 ${bannerStyle}`} data-testid="weather-hazard-banner">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle size={18} />
                  <span className="font-bold text-sm">Weather Warning — next 12h</span>
                </div>
                <div className="space-y-1.5">
                  {hazards.map((h, i) => {
                    const { Icon } = getHazardMeta(h.type);
                    return (
                      <div key={i} className="flex items-center gap-2 text-sm font-medium" data-testid={`hazard-alert-${h.type}`}>
                        <Icon size={15} />
                        <span>{h.label}</span>
                        <span className="opacity-70">· {h.window}</span>
                        {h.severity === "high" && (
                          <Badge className="bg-red-600 text-white text-[10px] px-1.5 py-0">HIGH</Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <Tabs defaultValue="metar" className="w-full">
              <TabsList className="grid w-full grid-cols-4 mb-4">
                <TabsTrigger value="metar" className="text-xs" data-testid="metar-tab">
                  <Thermometer size={14} className="mr-1" /> METAR
                </TabsTrigger>
                <TabsTrigger value="taf" className="text-xs" data-testid="taf-tab">
                  <FileText size={14} className="mr-1" /> TAF
                </TabsTrigger>
                <TabsTrigger value="snowtam" className="text-xs" data-testid="snowtam-tab">
                  <Snowflake size={14} className="mr-1" /> SNOWTAM
                </TabsTrigger>
                <TabsTrigger value="forecast" className="text-xs" data-testid="forecast-tab">
                  <CalendarClock size={14} className="mr-1" /> Outlook
                </TabsTrigger>
              </TabsList>

              {/* METAR Tab */}
              <TabsContent value="metar">
                {metarData ? (
                  <div className="space-y-4">
                    {/* Current Conditions */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {/* Temperature */}
                      <div className="bg-[#f8fafc] p-3 rounded-lg">
                        <div className="flex items-center gap-2 text-[#64748b] text-xs mb-1">
                          <Thermometer size={12} />
                          Temp
                        </div>
                        <p className="text-xl font-bold text-[#0f172a]">
                          {metarData.temp !== undefined ? `${metarData.temp}°C` : "N/A"}
                        </p>
                        {metarData.dewp !== undefined && (
                          <p className="text-xs text-[#64748b]">Dew: {metarData.dewp}°C</p>
                        )}
                      </div>

                      {/* Wind */}
                      <div className="bg-[#f8fafc] p-3 rounded-lg">
                        <div className="flex items-center gap-2 text-[#64748b] text-xs mb-1">
                          <Wind size={12} />
                          Wind
                        </div>
                        <p className="text-xl font-bold text-[#0f172a]">
                          {metarData.wspd !== undefined ? `${metarData.wspd} kt` : "Calm"}
                        </p>
                        <p className="text-xs text-[#64748b]">
                          {metarData.wdir !== undefined ? `${getWindDirection(metarData.wdir)} (${metarData.wdir}°)` : "Variable"}
                          {metarData.wgst ? ` G${metarData.wgst}` : ""}
                        </p>
                      </div>

                      {/* Visibility */}
                      <div className="bg-[#f8fafc] p-3 rounded-lg">
                        <div className="flex items-center gap-2 text-[#64748b] text-xs mb-1">
                          <Eye size={12} />
                          Visibility
                        </div>
                        <p className="text-xl font-bold text-[#0f172a]">
                          {visibilityLabel}
                        </p>
                      </div>

                      {/* Pressure */}
                      <div className="bg-[#f8fafc] p-3 rounded-lg">
                        <div className="flex items-center gap-2 text-[#64748b] text-xs mb-1">
                          <Droplets size={12} />
                          QNH
                        </div>
                        <p className="text-xl font-bold text-[#0f172a]">
                          {metarData.altim ? `${metarData.altim}` : "N/A"}
                        </p>
                        <p className="text-xs text-[#64748b]">hPa</p>
                      </div>
                    </div>

                    {/* Weather Conditions */}
                    {metarData.wx && (
                      <div className="flex items-center gap-2">
                        <Badge className="bg-amber-100 text-amber-700">{metarData.wx}</Badge>
                      </div>
                    )}

                    {/* Plain Language Translation */}
                    {metarLines.length > 0 && (
                      <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                        <div className="text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2">
                          {aiActive ? <Sparkles size={15} /> : <span>📖</span>}
                          METAR in Plain Language
                          {aiActive && <Badge className="bg-blue-600 text-white text-[10px] px-1.5 py-0">AI</Badge>}
                        </div>
                        <div className="space-y-2">
                          {metarLines.map((line, idx) => (
                            <p key={idx} className="text-sm text-blue-900">{line}</p>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Raw METAR */}
                    <div className="bg-[#0f172a] text-white p-3 rounded-lg">
                      <p className="text-xs text-[#94a3b8] mb-1">Raw METAR (Technical Code)</p>
                      <p className="font-mono text-xs break-all">{metarData.raw || "No data available"}</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6 text-[#64748b]">
                    <Cloud size={40} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">METAR data temporarily unavailable</p>
                  </div>
                )}
              </TabsContent>

              {/* TAF Tab */}
              <TabsContent value="taf">
                {tafData?.raw ? (
                  <div className="space-y-3">
                    {/* Plain Language Translation */}
                    {tafLines.length > 0 && (
                      <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                        <div className="text-sm font-semibold text-green-800 mb-3 flex items-center gap-2">
                          {aiActive ? <Sparkles size={15} /> : <span>📖</span>}
                          TAF in Plain Language (Weather Forecast)
                          {aiActive && <Badge className="bg-green-600 text-white text-[10px] px-1.5 py-0">AI</Badge>}
                        </div>
                        <div className="space-y-2">
                          {tafLines.map((line, idx) => (
                            <p key={idx} className="text-sm text-green-900">{line}</p>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Raw TAF */}
                    <div className="bg-[#0f172a] text-white p-4 rounded-lg">
                      <p className="text-xs text-[#94a3b8] mb-2 flex items-center gap-2">
                        <FileText size={12} /> Raw TAF (Technical Code)
                      </p>
                      <p className="font-mono text-xs break-all leading-relaxed whitespace-pre-wrap">
                        {tafData.raw}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6 text-[#64748b]">
                    <FileText size={40} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">TAF data not available</p>
                    <p className="text-xs mt-1">TAF may not be issued for all airports</p>
                  </div>
                )}
              </TabsContent>

              {/* SNOWTAM Tab */}
              <TabsContent value="snowtam">
                {snowtamData?.raw ? (
                  <div className="space-y-3">
                    {/* Plain Language Translation */}
                    {snowtamLines.length > 0 && (
                      <div className="bg-cyan-50 border border-cyan-200 p-4 rounded-lg">
                        <div className="text-sm font-semibold text-cyan-800 mb-3 flex items-center gap-2">
                          {aiActive ? <Sparkles size={15} /> : <span>📖</span>}
                          SNOWTAM in Plain Language
                          {aiActive && <Badge className="bg-cyan-600 text-white text-[10px] px-1.5 py-0">AI</Badge>}
                        </div>
                        <div className="space-y-2">
                          {snowtamLines.map((line, idx) => (
                            <p key={idx} className="text-sm text-cyan-900">{line}</p>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Raw SNOWTAM */}
                    <div className="bg-blue-900 text-white p-4 rounded-lg">
                      <p className="text-xs text-blue-200 mb-2 flex items-center gap-2">
                        <Snowflake size={12} /> Raw SNOWTAM (Technical Code)
                      </p>
                      <p className="font-mono text-xs break-all leading-relaxed whitespace-pre-wrap">
                        {snowtamData.raw}
                      </p>
                    </div>
                    <Badge className="bg-blue-100 text-blue-700">
                      <Snowflake size={12} className="mr-1" /> Active SNOWTAM
                    </Badge>
                  </div>
                ) : (
                  <div className="text-center py-6 text-[#64748b]">
                    <Snowflake size={40} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No active SNOWTAM</p>
                    <p className="text-xs mt-1">SNOWTAM is issued when snow/ice affects runway conditions</p>
                  </div>
                )}
              </TabsContent>

              {/* Forecast Outlook Tab (3h / 6h / 12h) */}
              <TabsContent value="forecast">
                {forecast?.available && forecast.slots?.length > 0 ? (
                  <div className="space-y-4">
                    <p className="text-xs text-[#64748b] flex items-center gap-1">
                      <CalendarClock size={12} /> Hourly outlook from {forecast.source || "Open-Meteo"} · times in UTC (Z)
                    </p>
                    <div className="grid grid-cols-3 gap-3" data-testid="forecast-slots">
                      {forecast.slots.map((slot) => (
                        <div
                          key={slot.label}
                          className="bg-[#f8fafc] border border-[#e2e8f0] rounded-lg p-3 text-center"
                          data-testid={`forecast-slot-${slot.label}`}
                        >
                          <p className="text-sm font-bold text-[#0f172a]">{slot.label}</p>
                          <p className="text-[11px] text-[#64748b] mb-2">{slot.time}</p>
                          <div className="text-3xl mb-1">{slot.emoji}</div>
                          <p className="text-xs font-medium text-[#0f172a] leading-tight min-h-[28px]">{slot.condition}</p>
                          <div className="mt-2 space-y-0.5 text-[11px] text-[#64748b]">
                            {slot.temp !== null && (
                              <p className="flex items-center justify-center gap-1"><Thermometer size={11} /> {slot.temp}°C</p>
                            )}
                            {slot.wind_spd !== null && (
                              <p className="flex items-center justify-center gap-1">
                                <Wind size={11} /> {slot.wind_dir !== null ? `${getWindDirection(slot.wind_dir)} ` : ""}{slot.wind_spd}kt
                                {slot.wind_gust ? ` G${slot.wind_gust}` : ""}
                              </p>
                            )}
                            {slot.vis_m !== null && (
                              <p className="flex items-center justify-center gap-1">
                                <Eye size={11} /> {slot.vis_m >= 10000 ? "10+ km" : `${(slot.vis_m / 1000).toFixed(1)} km`}
                              </p>
                            )}
                          </div>
                          {slot.hazards?.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1 justify-center">
                              {slot.hazards.map((hz) => {
                                const { Icon, chip } = getHazardMeta(hz);
                                return (
                                  <span key={hz} className={`inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full border ${chip}`}>
                                    <Icon size={10} /> {hz.replace("_", " ")}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Hazard summary list */}
                    {hazards.length > 0 ? (
                      <div className="space-y-2">
                        <p className="text-sm font-semibold text-[#0f172a]">Expected weather changes</p>
                        {hazards.map((h, i) => {
                          const { Icon, color } = getHazardMeta(h.type);
                          return (
                            <div key={i} className="flex items-center gap-2 bg-white border border-[#e2e8f0] rounded-lg p-2.5 text-sm">
                              <Icon size={16} className={color} />
                              <span className="font-medium text-[#0f172a]">{h.label}</span>
                              <span className="text-[#64748b]">· {h.window}</span>
                              <Badge className={`ml-auto text-[10px] px-1.5 py-0 ${h.severity === "high" ? "bg-red-600" : h.severity === "medium" ? "bg-amber-500" : "bg-yellow-400 text-[#0f172a]"} text-white`}>
                                {h.severity.toUpperCase()}
                              </Badge>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800 flex items-center gap-2">
                        <span>✅</span> No significant weather hazards expected in the next 12 hours.
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6 text-[#64748b]">
                    <CalendarClock size={40} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Forecast outlook temporarily unavailable</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* NOTAMs Card */}
        <Card className="border-[#e2e8f0] bg-white flex flex-col">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-[#0f172a] font-['Outfit']">
                <AlertTriangle size={20} className="text-amber-500" />
                EBCI NOTAMs
                {notams?.count > 0 && (
                  <Badge className="bg-amber-500 text-white ml-2">{notams.count} Active</Badge>
                )}
              </CardTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={fetchWeatherAndNotams}
                disabled={weatherLoading}
                className="h-8 w-8"
              >
                <RefreshCw size={16} className={weatherLoading ? "animate-spin" : ""} />
              </Button>
            </div>
            {notams?.source && (
              <p className="text-xs text-[#64748b]">Source: {notams.source}</p>
            )}
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden">
            {notams?.notams && notams.notams.length > 0 ? (
              <div className="h-full max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                <div className="space-y-3">
                  {notams.notams.map((notam, index) => (
                    <div key={index} className="bg-[#f8fafc] dark:bg-[#1e293b] p-3 rounded-lg border-l-4 border-amber-400">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <Badge variant="outline" className="text-xs font-mono bg-white dark:bg-[#0f172a] dark:text-gray-300">
                          {notam.number || notam.id || `NOTAM ${index + 1}`}
                        </Badge>
                        {notam.keyword && (
                          <Badge className="text-xs bg-[#64748b] text-white">
                            {notam.keyword}
                          </Badge>
                        )}
                      </div>
                      {notam.validity && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mb-1 font-medium">
                          {notam.validity}
                        </p>
                      )}
                      {notam.plain && (
                        <div className="mb-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded p-2">
                          <p className="text-sm text-amber-900 dark:text-amber-200 flex items-start gap-1.5">
                            <Sparkles size={13} className="mt-0.5 shrink-0 text-amber-600" />
                            <span>{notam.plain}</span>
                          </p>
                        </div>
                      )}
                      <p className="text-sm text-[#0f172a] dark:text-gray-200">
                        {notam.text || "No details available"}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-[#64748b]">
                <AlertTriangle size={40} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm mb-3">{notams?.message || "Loading NOTAMs..."}</p>
                <p className="text-xs text-[#94a3b8] mb-3">Check official sources for current NOTAMs:</p>
                <div className="flex flex-col gap-2">
                  {notams?.official_sources?.map((source, idx) => (
                    <a 
                      key={idx}
                      href={source.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#d4af37] hover:underline text-sm"
                    >
                      {source.name} →
                    </a>
                  )) || (
                    <a 
                      href="https://www.notaminfo.com/localnotam?icao=EBCI" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#d4af37] hover:underline text-sm"
                    >
                      View Official NOTAMs →
                    </a>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Follow Ground Movements Button */}
      <div className="mb-6" data-testid="ground-movements-section">
        <button
          onClick={() => setRadarOpen(true)}
          className="w-full group relative overflow-hidden rounded-lg border border-[#d4af37]/30 bg-gradient-to-r from-[#0f172a] to-[#1e293b] p-6 transition-all hover:border-[#d4af37]/60 hover:shadow-lg hover:shadow-[#d4af37]/10"
          data-testid="follow-ground-movements-btn"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30">
                <Radio size={28} className="text-[#d4af37]" />
              </div>
              <div className="text-left">
                <h3 className="text-lg font-bold text-white">Follow Ground Movements</h3>
                <p className="text-sm text-gray-400 mt-0.5">Live radar view — EBCI / CRL Charleroi Airport</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
              </span>
              <span className="text-sm text-gray-400 group-hover:text-[#d4af37] transition-colors">Open Live Radar</span>
              <ArrowRight size={18} className="text-gray-500 group-hover:text-[#d4af37] group-hover:translate-x-1 transition-all" />
            </div>
          </div>
        </button>
      </div>

      {/* Fullscreen Radar Popup — rendered via portal to overlay everything */}
      {radarOpen && createPortal(
        <div
          className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center"
          onClick={(e) => { if (e.target === e.currentTarget) setRadarOpen(false); }}
          data-testid="radar-popup-overlay"
        >
          <div className="relative w-[95vw] h-[90vh] bg-[#0f172a] rounded-xl border border-[#d4af37]/30 overflow-hidden shadow-2xl" data-testid="radar-popup">
            {/* Popup Header */}
            <div className="absolute top-0 left-0 right-0 z-[10000] bg-[#0f172a]/90 backdrop-blur-md px-5 py-3 flex items-center justify-between border-b border-[#1e293b]">
              <div className="flex items-center gap-3">
                <Radio size={18} className="text-[#d4af37]" />
                <span className="text-white font-medium">Live Radar — EBCI / CRL Charleroi Airport</span>
                <span className="relative flex h-2.5 w-2.5 ml-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                </span>
              </div>
              <button
                onClick={() => setRadarOpen(false)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                data-testid="close-radar-btn"
              >
                <span className="text-sm">Close</span>
                <X size={18} />
              </button>
            </div>
            {/* Radar Map — only renders when popup is open */}
            <div className="w-full h-full pt-12">
              <Suspense fallback={<div className="flex items-center justify-center h-full text-gray-400"><Loader2 className="animate-spin mr-2" size={20} /> Loading radar...</div>}>
                <RadarMap />
              </Suspense>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Windy Weather Section - Browser Style */}
      <div className="bg-white rounded-lg border border-[#e2e8f0] overflow-hidden mb-6">
        {/* Browser-like header */}
        <div className="bg-[#0f172a] px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
            </div>
            <div className="ml-4 flex items-center gap-2 bg-[#1e293b] rounded px-3 py-1">
              <Cloud size={14} className="text-[#d4af37]" />
              <span className="text-gray-300 text-sm">windy.com/airport/EBCI</span>
            </div>
          </div>
          <a
            href="https://www.windy.com/airport/EBCI?50.458,4.454,8"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#d4af37] hover:text-[#b5952f] text-sm flex items-center gap-1"
          >
            Open in New Tab <ArrowRight size={14} />
          </a>
        </div>
        
        {/* Windy Embed - Full Size */}
        <iframe
          src="https://embed.windy.com/embed2.html?lat=50.459&lon=4.453&detailLat=50.459&detailLon=4.453&width=1200&height=800&zoom=10&level=surface&overlay=wind&product=ecmwf&menu=true&message=true&marker=true&calendar=12&pressure=true&type=map&location=coordinates&detail=true&metricWind=kt&metricTemp=%C2%B0C&radarRange=-1"
          title="EBCI Weather"
          className="w-full"
          style={{ height: '750px' }}
          frameBorder="0"
          data-testid="windy-browser-embed"
        />
        
        {/* Footer with quick links */}
        <div className="bg-[#f8fafc] px-4 py-3 border-t border-[#e2e8f0] flex items-center justify-between">
          <p className="text-xs text-[#64748b]">
            Live weather data from Windy.com | Charleroi/Brussels-South (EBCI)
          </p>
          <div className="flex gap-2">
            <a
              href="https://www.windy.com/airport/EBCI/metar"
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
            >
              METAR
            </a>
            <a
              href="https://www.windy.com/airport/EBCI/taf"
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
            >
              TAF
            </a>
            <a
              href="https://www.windy.com/airport/EBCI/notam"
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1 bg-orange-600 text-white text-xs rounded hover:bg-orange-700"
            >
              NOTAMs
            </a>
            <a
              href="https://www.windy.com/airport/EBCI/webcams"
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700"
            >
              Webcams
            </a>
          </div>
        </div>
      </div>

      {/* EBCI Ground Movement Chart Download */}
      <div className="bg-white rounded-lg border border-[#e2e8f0] p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#0f172a] rounded-lg flex items-center justify-center">
              <FileText size={24} className="text-[#d4af37]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#0f172a]">EBCI Aerodrome Ground Movement Chart</h3>
              <p className="text-sm text-[#64748b]">CAO | Charleroi/Brussels-South | Issue: 22-JAN-2026</p>
            </div>
          </div>
          <a
            href="https://customer-assets.emergentagent.com/job_flightmanage/artifacts/2btzuz01_EBCI_GMC01_v35.pdf"
            download="EBCI_GMC01_v35.pdf"
            className="flex items-center gap-2 px-4 py-2 bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a] rounded-lg font-medium transition-colors"
            data-testid="download-gmc-pdf"
          >
            <Download size={18} />
            Download PDF
          </a>
        </div>
      </div>

      {/* Past Notes Dialog */}
      <Dialog open={pastNotesOpen} onOpenChange={setPastNotesOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] bg-[#1e293b] border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <History className="text-amber-500" size={20} />
              Past Flight Notes
              {pastNotes.length > 0 && (
                <Badge className="bg-amber-100 text-amber-700 ml-2">{filteredPastNotes.length} / {pastNotes.length} notes</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {pastNotes.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <Select value={noteYear} onValueChange={setNoteYear}>
                <SelectTrigger className="w-28 h-9 bg-[#0f172a] text-white border-gray-700" data-testid="past-notes-year">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All years</SelectItem>
                  {pastNoteYears.map((y) => (
                    <SelectItem key={y} value={y}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={noteMonth} onValueChange={setNoteMonth}>
                <SelectTrigger className="w-32 h-9 bg-[#0f172a] text-white border-gray-700" data-testid="past-notes-month">
                  <SelectValue placeholder="Month" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All months</SelectItem>
                  {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m, i) => (
                    <SelectItem key={m} value={String(i + 1).padStart(2, "0")}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="relative flex-1 min-w-[160px]">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
                <Input
                  value={noteSearch}
                  onChange={(e) => setNoteSearch(e.target.value)}
                  placeholder="Search reg or note..."
                  className="pl-8 h-9 bg-[#0f172a] text-white border-gray-700"
                  data-testid="past-notes-search"
                />
              </div>
            </div>
          )}
          <div className="mt-4">
            {pastNotesLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
              </div>
            ) : filteredPastNotes.length > 0 ? (
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-2">
                  {filteredPastNotes.map((note, idx) => (
                    <div 
                      key={idx} 
                      className="p-3 bg-[#0f172a] rounded-lg border border-gray-700 hover:border-amber-600/50 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-10 h-10 bg-amber-900/30 rounded-full flex items-center justify-center">
                          <Plane size={16} className="text-amber-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-amber-400 font-mono text-sm">{note.date}</span>
                            <span className="text-gray-500">/</span>
                            <span className="font-bold text-white">{note.registration}</span>
                          </div>
                          <p className="text-gray-300 text-sm">{note.note}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <div className="text-center py-10">
                <History size={48} className="mx-auto text-gray-600 mb-3" />
                <p className="text-gray-400">No past flight notes found</p>
                <p className="text-gray-500 text-sm mt-1">Notes from past flights will appear here</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
